import { MeasuredData } from "types/ISensorMeasuredData";

export interface IGanttChartData {
  name: string;
  data: IGanttChartSerieTemporal[];
  start: number;
  end: number;
  timezoneOffset: number;
  errors: number;
}
/**
 * What a Gantt row says when it does not draw a bar.
 *
 * `pastUnavailable` (aurora#1619): the row belongs to a past-date Gantt and there is
 * no result for that date -- before the engine served areas, or a gap in its passes.
 * It answers about the DATE, not about the weather and not about a job that will
 * finish: waiting does not fill it.
 */
export type GanttRowState =
  | "ready"
  | "doesNotOccur"
  | "neverWarms"
  | "calculating"
  | "notPlanned"
  | "unavailable"
  | "pastUnavailable";

export interface IGanttChartSerieTemporal {
  name: string;
  id: string;
  internalId?: number;
  color: string;
  parent?: string;
  resume: string;
  start: number;
  end: number;
  error?: string;
  environmentalVariables?: IGanttChartSerieEnvironmentalVariable[];
  isLocation?: boolean;
  isInsight?: boolean;
  isProbability?: boolean;
  probabilityType?: ProbabilityType;
  order?: number;
  probability?: string;
  /**
   * Any other value (a row of a retired type, e.g. one left in the database) is
   * drawn with no type icon and the area tooltip.
   */
  locationType?: "point" | "area";
  isProcessing?: boolean;
  error_message?: string;
  /**
   * Which of the things is true about this row (workspace#385, US2).
   * Present only on engine-served area rows, and on the area rows of a past-date
   * (retroacted) Gantt (aurora#1619).
   */
  rowState?: GanttRowState;
  /** Only for `neverWarms`: the variables no model family serves (FR-E02). */
  missingVariables?: string[];
}

export interface IGanttChartSerieEnvironmentalVariable {
  environmentalVariable: string;
  min?: number;
  max?: number;
  units?: string | null;
}

export interface IStation {
  station_id: number;
  region_id?: number;
  station_name: string;
  station_depth: number;
  station_depth_unit: string;
  station_lat: number;
  station_lon: number;
  station_save_by_user?: boolean;
  macro_region_name?: string;
  macro_region_origin?: string;
  station_main?: boolean;
  data_history?: boolean;
  environmentalTypes?: string[];
  measuredData?: MeasuredData[];
}

export interface IManageStationTable {
  stations: IStation[];
  stationCount: number;
  historyDataCount: number;
  limit: number;
  offset?: number;
}

export interface IStationsFilter {
  limit: number;
  offset?: number;
  station_description?: string;
  macroregion_description?: string;
}

export interface IEnvironmentalVariable {
  id: number;
  name: string;
  internal_name: string;
}

export interface IInsightChangeForUpdate {
  update: boolean;
  expiration: number;
}

export enum ProbabilityType {
  PERCENTAGE = "percentage",
  AGGREGATE = "aggregate",
}

export enum RiskLevel {
  EXTREME = "extreme",
  HIGH = "high",
  MODERATE = "moderate",
  LOW = "low",
}
